import { useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, X } from "lucide-react";
import { Field, Select, TextArea } from "@/components/FormField";
import { api } from "@/lib/api";
import { PROPERTY_TYPES, AREA_UNITS, NEEDS_OPTIONS, BUDGET_OPTIONS, TIMELINE_OPTIONS } from "../constants";
import { EXTRA_FIELDS_BY_KEY } from "../ExtraFields";

export default function SpaceDetails({ form, upd, updExtra, toggleNeed, extraFieldsKey }) {
  const [uploading, setUploading] = useState({ photo: false, document: false });
  const ExtraFieldsComponent = EXTRA_FIELDS_BY_KEY[extraFieldsKey];

  const handleFiles = async (fileList, kind) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const urls = [];
      for (const file of files) {
        const { url } = await api.upload(file, kind);
        urls.push(url);
      }
      const key = kind === "photo" ? "photos" : "documents";
      upd(key, [...form[key], ...urls]);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const removeFile = (key, url) => upd(key, form[key].filter((u) => u !== url));

  return (
    <div>
      <div className="font-serif-display text-xl text-[var(--yp-primary)]">Tell us about the space</div>
      <p className="text-sm text-[var(--yp-muted)] mt-1">The more we understand now, the more useful the site visit can be.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <Select id="sr-property" label="Property / site type" required value={form.property_type} onChange={(v) => upd("property_type", v)} options={PROPERTY_TYPES} />
        <div>
          <label className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Approximate area</label>
          <div className="mt-1 flex gap-2">
            <input data-testid="sr-area" type="number" min="0" value={form.area} onChange={(e) => upd("area", e.target.value)} className="flex-1 border border-[var(--yp-border)] rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--yp-primary)]" />
            <select data-testid="sr-area-unit" value={form.area_unit} onChange={(e) => upd("area_unit", e.target.value)} className="border border-[var(--yp-border)] rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--yp-primary)] bg-white">
              {AREA_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">What do you need?</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
            {NEEDS_OPTIONS.map((n) => (
              <label key={n} className="flex items-center gap-2 text-sm border border-[var(--yp-border)] rounded-lg px-3 py-2 cursor-pointer">
                <input type="checkbox" checked={form.needs.includes(n)} onChange={() => toggleNeed(n)} className="accent-[var(--yp-primary)]" />
                {n}
              </label>
            ))}
          </div>
        </div>

        <Select id="sr-budget" label="Approximate budget" value={form.budget} onChange={(v) => upd("budget", v)} options={BUDGET_OPTIONS} />
        <Select id="sr-timeline" label="When do you want to start?" value={form.timeline} onChange={(v) => upd("timeline", v)} options={TIMELINE_OPTIONS} />

        <TextArea id="sr-description" label="Describe your requirement" required value={form.description} onChange={(v) => upd("description", v)} placeholder="What are you trying to create, improve, plant or purchase?" className="sm:col-span-2" />

        {ExtraFieldsComponent && <ExtraFieldsComponent form={form} upd={upd} extra={form.extra} updExtra={updExtra} />}

        <UploadField
          label="Site photos" icon={Camera} kind="photo" files={form.photos}
          uploading={uploading.photo} onSelect={(files) => handleFiles(files, "photo")}
          onRemove={(url) => removeFile("photos", url)}
        />
        <UploadField
          label="Plan / drawing / BOQ" icon={FileText} kind="document" accept=".pdf,.jpg,.jpeg,.png" files={form.documents}
          uploading={uploading.document} onSelect={(files) => handleFiles(files, "document")}
          onRemove={(url) => removeFile("documents", url)}
        />
      </div>
    </div>
  );
}

function UploadField({ label, icon: Icon, kind, accept = "image/*", files, uploading, onSelect, onRemove }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">{label}</label>
      <label data-testid={`sr-upload-${kind}`} className="mt-1 flex items-center gap-2 border border-dashed border-[var(--yp-border)] rounded-xl px-3 py-3 text-sm text-[var(--yp-muted)] cursor-pointer hover:border-[var(--yp-primary)]">
        <Icon size={16} />
        {uploading ? "Uploading…" : "Add files"}
        <input type="file" multiple accept={accept} className="hidden" onChange={(e) => { onSelect(e.target.files); e.target.value = ""; }} disabled={uploading} />
      </label>
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {files.map((url) => (
            <span key={url} className="inline-flex items-center gap-1 chip text-[10px]">
              File <button type="button" onClick={() => onRemove(url)} aria-label="Remove file"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
